import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import './ArtistDetail.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://saskararecord.com/api';

function getHandle(url) {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/').filter(Boolean);
    return parts[0] ? `@${parts[0].replace('@', '')}` : url;
  } catch {
    return url;
  }
}

const PLATFORM_ICONS = {
  youtube: '▶',
  spotify: '♫',
  'apple music': '♪',
  tiktok: '♪',
  instagram: '📷',
  default: '🔗',
};

function platformIcon(name) {
  return PLATFORM_ICONS[name?.toLowerCase()] || PLATFORM_ICONS.default;
}

function PlatformIcon({ platform }) {
  const common = { width: 16, height: 16, style: { display: 'block' } };
  if (platform === 'instagram') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" stroke="none" />
      </svg>
    );
  }
  if (platform === 'tiktok') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="currentColor">
        <path d="M9 3v10.55A4 4 0 1 0 11 17V7h4V3H9z" />
      </svg>
    );
  }
  if (platform === 'youtube') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="currentColor">
        <rect x="2" y="5" width="20" height="14" rx="4" />
        <polygon points="10,9 16,12 10,15" fill="#18181B" />
      </svg>
    );
  }
  if (platform === 'spotify') {
    return (
      <svg {...common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M7 10c3-1 7-1 10 1" />
        <path d="M7.5 13c2.5-0.8 6-0.8 8.5 0.8" />
        <path d="M8 16c2-0.6 4.5-0.6 6.5 0.6" />
      </svg>
    );
  }
  return (
    <svg {...common} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

export default function ArtistDetail() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [expandedSong, setExpandedSong] = useState(null);
  const [hasLiked, setHasLiked] = useState(false);
  const [liking, setLiking] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    setHasLiked(localStorage.getItem(`saskara_liked_${slug}`) === '1');
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);
    const token = localStorage.getItem('saskara_user_token');
    axios
      .get(`${API_BASE}/artists/public/${slug}`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})
      .then((res) => {
        if (mounted) {
          setArtist(res.data.data);
          setFollowing(res.data.data.is_following || false);
        }
      })
      .catch(() => {
        if (mounted) setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [slug]);

  const handleLike = async () => {
    if (hasLiked || liking) return;
    setLiking(true);
    try {
      const res = await axios.post(`${API_BASE}/artists/public/${slug}/like`);
      setArtist((a) => ({ ...a, like_count: res.data.like_count }));
      localStorage.setItem(`saskara_liked_${slug}`, '1');
      setHasLiked(true);
    } catch (err) {
      console.error(err);
    } finally {
      setLiking(false);
    }
  };

  const handleFollow = async () => {
    const token = localStorage.getItem('saskara_user_token');
    if (!token) {
      window.location.href = `/login?redirect=/artists/${slug}`;
      return;
    }
    setFollowLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = following
        ? await axios.delete(`${API_BASE}/artists/public/${slug}/follow`, config)
        : await axios.post(`${API_BASE}/artists/public/${slug}/follow`, {}, config);
      setFollowing(res.data.following);
      setArtist((a) => ({ ...a, follower_count: res.data.follower_count }));
    } catch (err) {
      alert(err.response?.data?.error || 'Gagal follow artis');
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return <div className="artist-detail__state">Memuat...</div>;
  }

  if (notFound || !artist) {
    return (
      <div className="artist-detail__state">
        <p>Artis tidak ditemukan.</p>
        <Link to="/" className="artist-detail__back">← Kembali ke Beranda</Link>
      </div>
    );
  }

  const pageTitle = `${artist.name} — Saskara Record`;
  const pageDesc = artist.bio || `${artist.name}, artis ${artist.genre || 'musik'} di Saskara Record.`;
  const pageImage = artist.photo_url || 'https://saskararecord.com/logo512.png';
  const pageUrl = `https://saskararecord.com/artists/${slug}`;

  return (
    <>
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      <meta property="og:type" content="profile" />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:url" content={pageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={pageImage} />
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'MusicGroup',
          name: artist.name,
          genre: artist.genre || undefined,
          description: artist.bio || undefined,
          image: artist.photo_url || undefined,
          url: pageUrl,
        })}
      </script>
    <div className="artist-detail">
      <nav className="artist-detail__nav">
        <Link to="/" className="artist-detail__back">← Beranda</Link>
      </nav>

      <div className="artist-detail__hero">
        <div className="artist-detail__photo-wrap">
          <div className="artist-detail__photo">
            {artist.photo_url ? (
              <img src={artist.photo_url} alt={artist.name} />
            ) : (
              <div className="artist-detail__photo-fallback" />
            )}
            <span className="artist-detail__badge">Saskara Record Artist</span>
          </div>
          {artist.profile_video_url && (
            <div className="artist-detail__video-card">
              <video src={artist.profile_video_url} autoPlay muted loop playsInline />
            </div>
          )}
        </div>
        <div className="artist-detail__info">
          {artist.genre && <span className="artist-detail__genre">{artist.genre}</span>}
          <h1 className="artist-detail__name">{artist.name}</h1>

          <div className="artist-detail__stats">
            <span>👁 {artist.view_count || 0} views</span>
            <span>❤️ {artist.like_count || 0} likes</span>
            <span>👥 {artist.follower_count || 0} followers</span>
          </div>

          <div className="artist-detail__actions">
            <button
              className={`artist-detail__like-btn ${hasLiked ? 'is-liked' : ''}`}
              onClick={handleLike}
              disabled={hasLiked || liking}
            >
              {hasLiked ? '❤️ Disukai' : '🤍 Suka'}
            </button>
            <button
              className={`artist-detail__follow-btn ${following ? 'is-following' : ''}`}
              onClick={handleFollow}
              disabled={followLoading}
            >
              {following ? '✓ Following' : '+ Follow'}
            </button>
          </div>

          {artist.bio && <p className="artist-detail__bio">{artist.bio}</p>}

          {(artist.instagram_url || artist.tiktok_url || artist.spotify_url || artist.youtube_channel_id) && (
            <div className="artist-detail__socials-wrap">
              <p className="artist-detail__socials-label">Media Sosial</p>
              <div className="artist-detail__socials">
                {artist.instagram_url && (
                  <a href={artist.instagram_url} target="_blank" rel="noreferrer">
                    <span className="artist-detail__social-icon"><PlatformIcon platform="instagram" /></span>
                    {getHandle(artist.instagram_url)}
                  </a>
                )}
                {artist.tiktok_url && (
                  <a href={artist.tiktok_url} target="_blank" rel="noreferrer">
                    <span className="artist-detail__social-icon"><PlatformIcon platform="tiktok" /></span>
                    {getHandle(artist.tiktok_url)}
                  </a>
                )}
                {artist.spotify_url && (
                  <a href={artist.spotify_url} target="_blank" rel="noreferrer">
                    <span className="artist-detail__social-icon"><PlatformIcon platform="spotify" /></span>
                    Spotify
                  </a>
                )}
                {artist.youtube_channel_id && (
                  <a href={artist.youtube_channel_id} target="_blank" rel="noreferrer">
                    <span className="artist-detail__social-icon"><PlatformIcon platform="youtube" /></span>
                    YouTube
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {artist.songs && artist.songs.length > 0 && (
        <div className="artist-detail__songs">
          <h2>Diskografi</h2>
          <ul>
            {artist.songs.map((song) => (
              <li key={song.id} className="song-item">
                <button
                  className="song-item__header"
                  onClick={() => setExpandedSong(expandedSong === song.id ? null : song.id)}
                >
                  <div className="song-item__thumb">
                    {song.artwork_url ? (
                      <img src={song.artwork_url} alt={song.title} />
                    ) : (
                      <div className="song-item__thumb-fallback" />
                    )}
                  </div>
                  <div className="song-item__main">
                    <span className="song-item__title">{song.title}</span>
                    {song.release_date && (
                      <span className="song-item__year">
                        {new Date(song.release_date).getFullYear()}
                      </span>
                    )}
                  </div>
                  <span className="song-item__toggle">{expandedSong === song.id ? '−' : '+'}</span>
                </button>

                {expandedSong === song.id && (
                  <div className="song-item__detail">
                    {song.description && <p className="song-item__desc">{song.description}</p>}
                    {song.platforms && song.platforms.length > 0 ? (
                      <div className="song-item__platforms">
                        {song.platforms.map((p, i) => (
                          <a
                            key={i}
                            href={p.platform_url}
                            target="_blank"
                            rel="noreferrer"
                            className="song-item__platform-link"
                          >
                            <span>{platformIcon(p.platform_name)}</span> {p.platform_name}
                          </a>
                        ))}
                      </div>
                    ) : (
                      <p className="song-item__no-platform">Belum tersedia di platform manapun.</p>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
    </>
  );
}
